import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@telus-uds/components-web';

import {
  Table,
  Tr,
  Td,
  Th,
  TdImp
} from '../fmComponents/fmMicrosoftLicensing/css';
import EN from '../../../EN.json';
import { formatCurrency } from '../../utils/currencyFormatter';
import { managedDetectionResponse } from '../../utils/constant';

const FMProducts = ({ orderData }) => {
  const {
    srtDashboardFM: { managedItServicesFM, microsoftLicensingFM }
  } = EN;
  const {
    productSelectionFM,
    microsoftLicensingData: {
      selections: microsoftlicensingSelections = []
    } = {},
    managedItServicesSummary: {
      additionalCharges = [],
      charges: managedItServices = [],
      totalMonthlyCharges = 0
    } = {},
    discountValue,
    oneTimeDiscountValue,
    managedDetectionResponseData = {},
    overrideOnetimeDiscountValue,
    overrideMonthlyDiscountValue,
    appliedDiscountId,
  } = orderData;
  const {
    onboardingFee = 0,
    totalPrice = 0
  } = managedDetectionResponseData;
  const totals = useMemo(() => {
    let totalCostSum = 0;
    let totalPriceSum = 0;
    microsoftlicensingSelections.forEach(item => {
      totalCostSum += item.totalCost;
      totalPriceSum += item.totalPrice;
    });
    return {
      totalCost: totalCostSum,
      totalPrice: totalPriceSum
    };
  }, [microsoftlicensingSelections]);
  const itServicesTotal = useMemo(() => {
    let additionalChargesSum = 0;
    let managedItChargesSum = 0;
    let additionalOnboardingFee = 0;
    additionalCharges.forEach(item => {
      additionalChargesSum += item.total;
    });
    managedItServices.forEach(item => {
      managedItChargesSum += item.total;
    });
    additionalOnboardingFee = additionalChargesSum;
    additionalChargesSum = additionalChargesSum - (oneTimeDiscountValue || 0);
    return {
      totalAdditionalCharges: additionalChargesSum,
      totalManagedItCharges: managedItChargesSum,
      additionalOnboardingFee
    };
  }, [additionalCharges, managedItServices]);

  const additionalServicesTotalMonthlyCharges = useMemo(() => {
    const additionalServices = managedItServices.filter(item =>
      item.name.includes('Additional Service')
    );
    return additionalServices.reduce(
      (total, service) => total + service.total,
      0
    );
  }, [managedItServices]);

  const mdrPricing = {
    mdrOnboardingFee: managedDetectionResponseData.onboardingFee || 0,
    mdrTotalPrice: managedDetectionResponseData.totalPrice || 0,
    mdrGrandTotal: managedDetectionResponseData.grandTotal || managedDetectionResponseData.totalPrice || 0
};

  // const managedServicesTotalMonthlyCharges = useMemo(() => {
  //   const managedServices = managedItServices.filter(
  //     item => !item.name.includes('Additional Service')
  //   );
  //   return managedServices.reduce((total, service) => total + service.total, 0);
  // }, [managedItServices]);

  const totalMonthlyChargesWithoutAdditionalServices =
    totalMonthlyCharges - additionalServicesTotalMonthlyCharges;

  const grandTotal =
    totalMonthlyChargesWithoutAdditionalServices +
    additionalServicesTotalMonthlyCharges +
    mdrPricing.mdrGrandTotal +
    (totals ? totals.totalPrice : 0) -
    (discountValue ? parseFloat(discountValue) : 0);

  return (
    <div>
      {productSelectionFM.includes(managedItServicesFM[0]) && (
        <>
          {managedItServices && managedItServices.length && (
            <Table>
              <Th><Typography>Managed IT Services</Typography></Th>
              <Th><Typography>Qty</Typography></Th>
              {managedItServices
                .filter(item => !item.name.includes('Additional Service'))
                .map(({ name, quantity }) => (
                  <Tr key={name}>
                    <Td><Typography>{name}</Typography></Td>
                    <Td><Typography>{quantity}</Typography></Td>
                  </Tr>
                ))}
              <Tr>
                <TdImp colSpan={1}><Typography>Total Monthly Charges</Typography></TdImp>
                <TdImp>
                  <Typography>{formatCurrency(totalMonthlyChargesWithoutAdditionalServices)}</Typography>
                </TdImp>
              </Tr>
            </Table>
          )}
          {/* MDR Summary */}
          {productSelectionFM.includes(managedDetectionResponse) &&
            managedDetectionResponseData &&
            Object.keys(managedDetectionResponseData).length && (
              <Table>
                <Th><Typography>{managedDetectionResponse}</Typography></Th>
                <Th><Typography>Qty</Typography></Th>

                <Tr key={managedDetectionResponseData.mdrData.name}>
                  <Td><Typography>{managedDetectionResponseData.mdrData.name}</Typography></Td>
                  <Td><Typography>{managedDetectionResponseData.numberOfUsers}</Typography></Td>
                </Tr>

                <Tr>
                  <TdImp colSpan={1}><Typography>Total Monthly Charges</Typography></TdImp>
                  <TdImp>
                    <Typography>{formatCurrency(mdrPricing.mdrTotalPrice || 0)}</Typography>  {/* Old orders may not have grand total as this was added later */}
                  </TdImp>
                </Tr>
              </Table>
            )}
          {productSelectionFM.includes(microsoftLicensingFM[0]) && (
            <>
              {!!microsoftlicensingSelections.length && (
                <Table>
                  <Th><Typography>Microsoft Licensing Subscriptions</Typography></Th>
                  <Th><Typography>Cost</Typography></Th>
                  <Th><Typography>Price Per Unit</Typography></Th>
                  <Th><Typography>Qty</Typography></Th>
                  <Th><Typography>Total Charge Per Month For 1 Year</Typography></Th>
                  {microsoftlicensingSelections.map(
                    ({
                      subscriptionLineOption,
                      cost,
                      price,
                      qtyOfLicenses,
                      totalPrice
                    }) => (
                      <Tr key={subscriptionLineOption}>
                        <Td><Typography>{subscriptionLineOption}</Typography></Td>
                        <Td><Typography>{formatCurrency(cost)}</Typography></Td>
                        <Td><Typography>{formatCurrency(price)}</Typography></Td>
                        <Td><Typography>{qtyOfLicenses}</Typography></Td>
                        <Td><Typography>{formatCurrency(totalPrice)}</Typography></Td>
                      </Tr>
                    )
                  )}
                  <Tr>
                    <TdImp colSpan={4}><Typography>Total Monthly Charges</Typography></TdImp>
                    <TdImp><Typography>{formatCurrency(totals.totalPrice)}</Typography></TdImp>
                  </Tr>
                </Table>
              )}
            </>
          )}
          {managedItServices &&
            managedItServices.length &&
            managedItServices.some(item =>
              item.name.includes('Additional Service')
            ) && (
              <Table>
                <Th><Typography>Additional Services</Typography></Th>
                <Th><Typography>Qty</Typography></Th>
                <Th><Typography>Monthly Charges</Typography></Th>
                {managedItServices
                  .filter(item => item.name.includes('Additional Service'))
                  .map(({ name, quantity, total }) => (
                    <Tr key={name}>
                      <Td><Typography>{name}</Typography></Td>
                      <Td><Typography>{quantity}</Typography></Td>
                      <Td><Typography>{formatCurrency(total)}</Typography></Td>
                    </Tr>
                  ))}
                <Tr>
                  <TdImp colSpan={2}><Typography>Monthly Additional Services</Typography></TdImp>
                  <TdImp>
                    <Typography>{formatCurrency(additionalServicesTotalMonthlyCharges)}</Typography>
                  </TdImp>
                </Tr>
              </Table>
            )}
          {additionalCharges && additionalCharges.length && (
            <Table>
              <Th><Typography>Additional Charges</Typography></Th>
              <Th><Typography>One Time Charges</Typography></Th>
              {additionalCharges.map(({ name, total }) => (
                <Tr key={name}>
                  <Td><Typography>{name}</Typography></Td>
                  <Td><Typography>{formatCurrency(total)}</Typography></Td>
                </Tr>
              ))}
              {productSelectionFM.includes(managedDetectionResponse) &&
                managedDetectionResponseData &&
                Object.keys(managedDetectionResponseData).length && (
                  <Tr key={managedDetectionResponseData.feeData.name}>
                    <Td>
                    <Typography>MDR Services Onboarding Fee</Typography>
                    </Td>
                    <Td>
                      <Typography>{formatCurrency(
                        mdrPricing.mdrOnboardingFee
                      )}</Typography>
                    </Td>
                  </Tr>
                )}
              <Tr>
                <TdImp colSpan={1}><Typography>Total One Time Charges</Typography></TdImp>
                <TdImp>
                  <Typography>{formatCurrency(itServicesTotal.additionalOnboardingFee+mdrPricing.mdrOnboardingFee)}</Typography>
                </TdImp>
              </Tr>
            </Table>
          )}
        </>
      )}
      {appliedDiscountId && (
        <Table>
          <Th><Typography>Discount</Typography></Th>
          <Th><Typography>Total Discount Per month for 1 year</Typography></Th>
          <Tr>
            <TdImp><Typography>Total Monthly Discount</Typography></TdImp>
            <TdImp><Typography>${overrideMonthlyDiscountValue || discountValue}</Typography></TdImp>
          </Tr>
          <Tr>
            <TdImp><Typography>Total One Time Discount</Typography></TdImp>
            <TdImp><Typography>${overrideOnetimeDiscountValue || oneTimeDiscountValue}</Typography></TdImp>
          </Tr>
          <Tr>
            <TdImp><Typography>Approval Id</Typography></TdImp>
            <TdImp><Typography>{appliedDiscountId}</Typography></TdImp>
          </Tr>
        </Table>
      )}
      <Table>
        <Th><Typography>Order Summary</Typography></Th>
        <Th><Typography>Total Charges</Typography></Th>
        <Th><Typography>Discounts</Typography></Th>
        {managedItServices && managedItServices.length && (
          <Tr>
            <TdImp><Typography>Monthly Managed IT Services</Typography></TdImp>
            <TdImp>
              <Typography>{formatCurrency(totalMonthlyChargesWithoutAdditionalServices)}</Typography>
            </TdImp>
            <TdImp></TdImp>
          </Tr>
        )}
        {productSelectionFM.includes(microsoftLicensingFM[0]) && (
          <>
            {!!microsoftlicensingSelections.length && (
              <Tr>
                <TdImp><Typography>Monthly Microsoft Licensing</Typography></TdImp>
                <TdImp><Typography>{formatCurrency(totals.totalPrice)}</Typography></TdImp>
                <TdImp></TdImp>
              </Tr>
            )}
          </>
        )}
        {managedItServices &&
          managedItServices.length &&
          managedItServices.some(item =>
            item.name.includes('Additional Service')
          ) && (
            <Tr>
              <TdImp><Typography>Monthly Additional Services</Typography></TdImp>
              <TdImp>
                <Typography>{formatCurrency(additionalServicesTotalMonthlyCharges)}</Typography>
              </TdImp>
              <TdImp></TdImp>
            </Tr>
          )}
        {productSelectionFM.includes(managedDetectionResponse) &&
          managedDetectionResponseData &&
          (Object.keys(managedDetectionResponseData).length >0) && (
            <Tr key={managedDetectionResponseData.mdrData.id}>
              <TdImp><Typography>Monthly MDR Services</Typography></TdImp>
              <TdImp>
                <Typography>{formatCurrency(mdrPricing.mdrTotalPrice) || 0}</Typography> {/* Old orders may not have grand total as this was added later */}
              </TdImp>
              <TdImp></TdImp>
            </Tr>
          )}
           {discountValue && (
            <Tr>
              <TdImp><Typography>Monthly Discount</Typography></TdImp>
              <TdImp></TdImp>
              <TdImp><Typography>-${discountValue}</Typography></TdImp>
            </Tr>
          )}
        {managedDetectionResponseData && managedDetectionResponseData.selectedMdrPromotions && (<Tr key={managedDetectionResponseData.selectedMdrPromotions[0].mdrMonthlyPromotionTotal}>
            <TdImp><Typography>Monthly MDR Services Discount</Typography></TdImp>
            <TdImp></TdImp>
            <TdImp><Typography>-{managedDetectionResponseData.selectedMdrPromotions.mdrMonthlyPromotionTotal}</Typography></TdImp>
        </Tr>)}
        {additionalCharges &&
          additionalCharges.length &&
          additionalCharges.map(({ name, total }) => (
            <Tr key={name}>
              <TdImp><Typography>{name}</Typography></TdImp>
              <TdImp><Typography>{formatCurrency(total)}</Typography></TdImp>
              <TdImp></TdImp>
            </Tr>
          ))}
        {productSelectionFM.includes(managedDetectionResponse) &&
          managedDetectionResponseData &&
          Object.keys(managedDetectionResponseData).length && (
            <Tr key={managedDetectionResponseData.feeData.name}>
              <TdImp>
              <Typography>MDR Services Onboarding Fee</Typography>
              </TdImp>
              <TdImp>
                <Typography>{formatCurrency(managedDetectionResponseData.onboardingFee)}</Typography>
              </TdImp>
              <TdImp></TdImp>
            </Tr>
          )}
        {oneTimeDiscountValue && (
          <Tr>
            <TdImp><Typography>One Time Discount</Typography></TdImp>
            <TdImp></TdImp>
            <TdImp><Typography>-${oneTimeDiscountValue}</Typography></TdImp>
          </Tr>
        )}
        <Tr>
          <TdImp><Typography>Total Monthly Charges (CAD)</Typography></TdImp>
          <TdImp><Typography>{formatCurrency(grandTotal)}</Typography></TdImp>
          <TdImp></TdImp>
        </Tr>
        <Tr>
          <TdImp><Typography>Total One Time Charges (CAD)</Typography></TdImp>
          <TdImp>
            <Typography>{formatCurrency(
                itServicesTotal.totalAdditionalCharges + mdrPricing.mdrOnboardingFee
            )}</Typography>
          </TdImp>
            <TdImp></TdImp>
        </Tr>
      </Table>
      {orderData &&
        orderData.remediationProject &&
        orderData.remediationProject.toUpperCase() === 'YES' && (
          <div style={{ marginTop: '10px' }}>
            <Typography block>
            <strong style={{ fontWeight: 600 }}>
              Estimated Remediation Project ({orderData.remediationCurrency}):
            </strong>{' '}
            $
            {orderData.remediationMonthlyCharge
              ? orderData.remediationMonthlyCharge
              : orderData.remediationOneTimeCharge}{' '}
            ({orderData.remediationMonthlyCharge ? 'Monthly' : 'One Time'})
            </Typography>
          </div>
        )}
    </div>
  );
};

FMProducts.propTypes = {
  orderData: PropTypes.shape({
    productSelectionFM: PropTypes.arrayOf(PropTypes.string),
    microsoftLicensingData: {
      selections: PropTypes.array
    },
    managedItServicesSummary: {
      additionalCharges: PropTypes.array,
      charges: PropTypes.array
    },
    discountValue: PropTypes.string,
    discountApprovalId: PropTypes.string,
    oneTimeDiscountValue: PropTypes.string,
    overrideMonthlyDiscountValue: PropTypes.string,
    overrideOnetimeDiscountValue: PropTypes.string,
    appliedDiscountId: PropTypes.string,
  }).isRequired
};

export default FMProducts;
